import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FiShoppingBag } from "react-icons/fi";
import { FaShoppingCart, FaLeaf, FaTruck, FaStar, FaCheckCircle, FaHeart, FaSeedling, FaChartBar, FaAward } from "react-icons/fa";
import { products } from "../data/productsData";
import "./ProductDetail.css";

// ---------------------------------------------------------------------------
// Render SVG star icons for a given rating (supports half-stars)
// ---------------------------------------------------------------------------
const renderStars = (rating) =>
  [1, 2, 3, 4, 5].map((star) => {
    const fill =
      rating >= star ? "#f59e0b" : rating >= star - 0.5 ? "#fbbf24" : "#d1d5db";
    return (
      <svg
        key={star}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill={fill}
        style={{ marginRight: 2 }}
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    );
  });

// ---------------------------------------------------------------------------
// ProductDetail Component
// Reads :id from the URL via useParams → finds the product in the shared
// products array → renders the full detail page for that product.
// ---------------------------------------------------------------------------
const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // Resolve current product: match by id param, fallback to first item
  // ------------------------------------------------------------------
  const product = products.find((p) => p.id === parseInt(id)) || products[0];

  // Component state
  const [selectedImage, setSelectedImage] = useState(product.images[0]);
  const [selectedWeight, setSelectedWeight] = useState(
    product.weights.find((w) => w.popular) || product.weights[0]
  );
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [pincode, setPincode] = useState("");
  const [deliveryMessage, setDeliveryMessage] = useState("");

  // Reset state when navigating to a different product
  useEffect(() => {
    setSelectedImage(product.images[0]);
    setSelectedWeight(product.weights.find((w) => w.popular) || product.weights[0]);
    setQuantity(1);
    setActiveTab("description");
    setIsWishlisted(false);
    setDeliveryMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [product.id]);

  // Quantity handlers
  const handleQuantityChange = (delta) =>
    setQuantity((prev) => Math.max(1, Math.min(30, prev + delta)));

  // Add to Cart
  const handleAddToCart = () => {
    const total = selectedWeight.price * quantity;
    Swal.fire({
      icon: "success",
      title: "Added to Cart!",
      html: `
        <div style="font-size:14.5px;color:#24201e;margin-top:8px;">
          <strong>${product.name}</strong> (${selectedWeight.weight})<br/>
          Qty: <strong>${quantity}</strong> &nbsp; &nbsp; Total: <strong style="color:#1d4834;">₹${total}</strong>
        </div>`,
      confirmButtonText: "Continue Shopping",
      confirmButtonColor: "#1d4834",
      showCancelButton: true,
      cancelButtonText: "View Cart",
      cancelButtonColor: "#c4551d",
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) navigate("/");
    });
  };

  // Buy Now
  const handleBuyNow = () => {
    Swal.fire({
      icon: "info",
      title: "Proceeding to Checkout",
      text: `Buying ${quantity} × ${product.name} (${selectedWeight.weight}) for ₹${selectedWeight.price * quantity}`,
      timer: 1600,
      showConfirmButton: false,
    });
    setTimeout(() => {
      navigate("/ShopCheckOut", {
        state: { product, weight: selectedWeight, quantity, totalPrice: selectedWeight.price * quantity },
      });
    }, 1600);
  };

  // Wishlist toggle
  const handleWishlistToggle = () => {
    setIsWishlisted((prev) => !prev);
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: isWishlisted ? "info" : "success",
      title: isWishlisted ? "Removed from Wishlist" : "Saved to Wishlist!",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  // Pincode delivery check
  const handlePincodeCheck = (e) => {
    e.preventDefault();
    setDeliveryMessage(
      pincode.trim().length === 6 && /^\d+$/.test(pincode.trim())
        ? "Sorry! Delivery is currently unavailable at your pincode."
        : "Please enter a valid 6-digit Indian PIN code."
    );
  };
  // Related products — filter out current, show up to 4
  const relatedProducts = products.filter((p) => p.id !== product.id).slice(0, 5);

  // Scroll to tabs (for reviews link)
  const scrollToTabs = (tab) => {
    setActiveTab(tab);
    document.getElementById("kvd-tabs-anchor")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="kvd-wrapper">

      {/* ============================================================
          BREADCRUMBS
         ============================================================ */}
      <div className="kvd-breadcrumb-bar">
        <div className="kvd-container">
          <div className="kvd-breadcrumbs">
            <Link to="/Home/" className="kvd-breadcrumb-link">Home</Link>
            <span className="kvd-breadcrumb-sep">/</span>
            <Link to="/ProductList" className="kvd-breadcrumb-link">Products</Link>
            <span className="kvd-breadcrumb-sep">/</span>
            <span className="kvd-breadcrumb-link">{product.category}</span>
            <span className="kvd-breadcrumb-sep">/</span>
            <span className="kvd-breadcrumb-current">{product.name}</span>
          </div>
        </div>
      </div>

      {/* ============================================================
          MAIN PRODUCT SHOWCASE
         ============================================================ */}
      <div className="kvd-container">
        <div className="kvd-product-grid">

          {/* ----------------------- GALLERY COL ----------------------- */}
          <div className="kvd-gallery-col">
            <div className="kvd-main-image-card">
              {/* Badges */}
              <div className="kvd-image-badges">
                {product.badge && (
                  <span
                    className={`kvd-badge ${product.badge === "Hot" || product.badge === "Sale"
                      ? "kvd-badge-discount"
                      : product.badge === "New"
                        ? "kvd-badge-organic"
                        : "kvd-badge-bestseller"
                      }`}
                  >
                    <FaStar /> {product.badge}
                  </span>
                )}
                {selectedWeight.discount && (
                  <span className="kvd-badge kvd-badge-discount">
                    {selectedWeight.discount}
                  </span>
                )}
              </div>

              {/* Wishlist float button */}
              <button
                type="button"
                className={`kvd-wishlist-float ${isWishlisted ? "active" : ""}`}
                onClick={handleWishlistToggle}
                title={isWishlisted ? "In Wishlist" : "Add to Wishlist"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                  fill={isWishlisted ? "currentColor" : "none"}
                  stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>

              </button>

              {/* Main image */}
              <img
                src={selectedImage}
                alt={`${product.name} — KunjVeda`}
                className="kvd-main-image"
                loading="eager"
              />
            </div>

            {/* Thumbnail strip */}
            <div className="kvd-thumbnails-strip">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`kvd-thumb-btn ${selectedImage === img ? "active" : ""}`}
                  onClick={() => setSelectedImage(img)}
                >
                  <img src={img} alt={`${product.name} view ${idx + 1}`} />
                </button>
              ))}
            </div>

            {/* Trust strip */}
            <div className="kvd-trust-banner">
              <div className="kvd-trust-item">
                <div className="kvd-trust-icon"><FaLeaf /></div>
                <div className="kvd-trust-text">
                  <h5>100% Pure</h5>
                  <p>Direct farm fresh</p>
                </div>
              </div>
              <div className="kvd-trust-item">
                <div className="kvd-trust-icon"><FaCheckCircle /></div>
                <div className="kvd-trust-text">
                  <h5>Lab Tested</h5>
                  <p>FSSAI certified</p>
                </div>
              </div>
              <div className="kvd-trust-item">
                <div className="kvd-trust-icon"><FaTruck /></div>
                <div className="kvd-trust-text">
                  <h5>Free Delivery</h5>
                  <p>on all Orders</p>
                </div>
              </div>
            </div>
          </div>

          {/* ----------------------- INFO COL ----------------------- */}
          <div className="kvd-info-col">
            <span className="kvd-brand-pill">KunjVeda Authentic Spice</span>

            <h1 className="kvd-product-title">{product.name}</h1>
            <p className="kvd-product-tagline">{product.tagline}</p>

            {/* Rating row */}
            <div className="kvd-rating-row">
              <div className="kvd-stars">{renderStars(product.rating)}</div>
              <span className="kvd-rating-score">{product.rating}</span>
              <span className="kvd-reviews-count" onClick={() => scrollToTabs("reviews")}>
                ({product.reviews} reviews)
              </span>
              <div className="kvd-stock-badge">
                <span className="kvd-stock-pulse"></span>
                <span>In Stock</span>
              </div>
            </div>

            {/* Price box */}
            <div className="kvd-price-box">
              <div className="kvd-price-row">
                <span className="kvd-currency">₹</span>
                <span className="kvd-current-price">{selectedWeight.price * quantity}</span>
                {selectedWeight.oldPrice > selectedWeight.price && (
                  <span className="kvd-old-price">₹{selectedWeight.oldPrice * quantity}</span>
                )}
                {selectedWeight.discount && (
                  <span className="kvd-save-pill">
                    Save ₹{(selectedWeight.oldPrice - selectedWeight.price) * quantity} ({selectedWeight.discount})
                  </span>
                )}
              </div>
              <p className="kvd-tax-note">
                Inclusive of all taxes &nbsp;•&nbsp; <strong>Free delivery</strong> on orders
              </p>
            </div>

            {/* Description */}
            <p className="kvd-description-brief">{product.description}</p>

            {/* Weight options */}
            <div className="kvd-section-label">
              <span>Select Pack Size</span>
              <span className="kvd-selected-val">{selectedWeight.weight}</span>
            </div>
            <div className="kvd-weight-options">
              {product.weights.map((w, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`kvd-weight-btn ${selectedWeight.weight === w.weight ? "active" : ""}`}
                  onClick={() => setSelectedWeight(w)}
                >
                  <span className="kvd-weight-title">{w.weight}</span>
                  <span className="kvd-weight-price">₹{w.price}</span>
                </button>
              ))}
            </div>

            {/* Quantity + CTA buttons */}
            <div className="kvd-actions-container">
              {/* <div className="kvd-qty-row">
                <span className="kvd-section-label" style={{ margin: 0 }}>Quantity</span>
                <div className="kvd-qty-selector">
                  <button
                    type="button"
                    className="kvd-qty-btn"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                  >−</button>
                  <input
                    type="text"
                    className="kvd-qty-input"
                    value={quantity}
                    readOnly
                  />
                  <button
                    type="button"
                    className="kvd-qty-btn"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= 30}
                  >+</button>
                </div>
                <div className="kvd-total-preview">
                  Total: <strong>₹{selectedWeight.price * quantity}</strong>
                </div>
              </div> */}

              {/* <div className="kvd-cta-group"> */}
              <div>
                <button type="button" className="kvd-btn-cart" onClick={() => navigate("/Contact")}>
                  <FiShoppingBag />
                  Enquire Now
                </button>
                {/* <button type="button" className="kvd-btn-buy" onClick={handleBuyNow}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                    fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"
                  >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                  Buy Now
                </button> */}
              </div>
            </div>

            {/* Pincode checker */}
            <div className="kvd-pincode-card">
              <div className="kvd-pincode-header">
                Check Delivery to Your Pincode
              </div>
              <form onSubmit={handlePincodeCheck} className="kvd-pincode-form">
                <input
                  type="text"
                  maxLength={6}
                  placeholder="Enter 6-digit Pincode"
                  className="kvd-pincode-input"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                />
                <button type="submit" className="kvd-pincode-btn">Check</button>
              </form>
              {deliveryMessage && <p className="kvd-pincode-result">{deliveryMessage}</p>}
            </div>
          </div>
        </div>

        {/* ============================================================
            TABBED INFO SECTION
           ============================================================ */}
        <div className="kvd-tabs-section" id="kvd-tabs-anchor">
          <div className="kvd-tabs-header">
            {[
              { key: "description", label: "Overview & Purity", icon: <FaLeaf /> },
              { key: "benefits", label: "Ayurvedic Benefits", icon: <FaSeedling /> },
              { key: "nutrition", label: "Nutritional Facts", icon: <FaChartBar /> },
              { key: "reviews", label: `Reviews (${product.reviews})`, icon: <FaStar /> },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`kvd-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span style={{ marginRight: "8px" }}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ---- Tab 1: Overview ---- */}
          {activeTab === "description" && (
            <div className="kvd-tab-pane">
              <h3 className="kvd-tab-title">Crafted with Purity | From Farm to Kitchen</h3>
              <p className="kvd-tab-text">{product.description}</p>
              <p className="kvd-tab-text">
                <strong>Origin:</strong> {product.origin} <br />
                <strong>Shelf Life:</strong> {product.shelfLife} <br />
                <strong>Dietary:</strong> {product.dietary}
              </p>
              <div className="kvd-features-grid">
                {product.features.map((item, idx) => (
                  <div key={idx} className="kvd-feature-card">
                    <span className="kvd-feature-icon"><FaAward /></span>
                    <div>
                      <h6>{item.title}</h6>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---- Tab 2: Ayurvedic Benefits ---- */}
          {activeTab === "benefits" && (
            <div className="kvd-tab-pane">
              <h3 className="kvd-tab-title">Time-Tested Ayurvedic Wellness Properties</h3>
              <p className="kvd-tab-text">
                In classical Ayurveda, spices are revered as medicinal nourishment that kindles
                digestive fire, clears metabolic toxins, and restores constitutional harmony.
              </p>
              <div className="kvd-features-grid">
                {product.ayurvedicBenefits.map((benefit, idx) => (
                  <div key={idx} className="kvd-feature-card">
                    <span className="kvd-feature-icon"> <FaSeedling /> </span>
                    <div>
                      <h6>Benefit #{idx + 1}</h6>
                      <p>{benefit}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---- Tab 3: Nutritional Facts ---- */}
          {activeTab === "nutrition" && (
            <div className="kvd-tab-pane">
              <h3 className="kvd-tab-title">Nutritional Breakdown (per 100g approx.)</h3>
              <p className="kvd-tab-text">
                Zero artificial preservatives, zero added sugar, zero hydrogenated fats, zero synthetic dyes.
              </p>
              <table className="kvd-nutrition-table">
                <thead>
                  <tr><th>Nutrient</th><th>Value per 100g</th></tr>
                </thead>
                <tbody>
                  <tr><td>Energy</td><td>{product.nutrition.energy}</td></tr>
                  <tr><td>Carbohydrates</td><td>{product.nutrition.carbohydrates}</td></tr>
                  <tr><td>Protein</td><td>{product.nutrition.protein}</td></tr>
                  <tr><td>Fat</td><td>{product.nutrition.fat}</td></tr>
                  <tr>
                    <td>Key Active Compound</td>
                    <td><strong>{product.nutrition.activeCompound}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* ---- Tab 4: Reviews ---- */}
          {activeTab === "reviews" && (
            <div className="kvd-tab-pane">
              <div className="kvd-reviews-summary">
                <div className="kvd-big-score">{product.rating}</div>
                <div>
                  <div className="kvd-stars">{renderStars(product.rating)}</div>
                  <p style={{ margin: "4px 0 0", color: "#6a615b", fontSize: 14 }}>
                    Based on {product.reviews} verified buyer ratings across India.
                  </p>
                </div>
              </div>
              <div className="kvd-reviews-list">
                {[
                  { name: "Aarav S. — Mumbai", stars: 5, text: "Unbelievable natural aroma! The color is deep and vibrant — a small spoon does wonders in my daily curry." },
                  { name: "Pooja M. — Bengaluru", stars: 5, text: "Fast 2-day delivery and airtight packaging. Loved the fresh fragrant taste. Will definitely re-order." },
                  { name: "Ramesh K. — Delhi NCR", stars: 4.5, text: "Pure and authentic as promised. Highly recommend KunjVeda to anyone who values chemical-free spices." },
                ].map((r, idx) => (
                  <div key={idx} className="kvd-review-card">
                    <div className="kvd-reviewer-header">
                      <span className="kvd-reviewer-name">{r.name}</span>
                      <span className="kvd-verified-badge"><FaStar /> Verified Buyer</span>
                    </div>
                    <div className="kvd-stars" style={{ marginBottom: 6 }}>{renderStars(r.stars)}</div>
                    <p className="kvd-review-comment">"{r.text}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ============================================================
            RELATED PRODUCTS SECTION
            Clicking a related card navigates to its detail page
           ============================================================ */}
        <div className="kvd-related-section">
          <div className="kvd-section-heading">
            <h3>Explore More Pure KunjVeda Spices</h3>
            <p>Hand-harvested, stone-ground, and packed with authentic aroma</p>
          </div>
          <div className="kvd-related-grid">
            {relatedProducts.map((spice) => (
              <div
                key={spice.id}
                className="kvd-related-card"
                onClick={() => navigate(`/ProductList/ProductDetail/${spice.id}`)}
                style={{ cursor: "pointer" }}
              >
                <div className="kvd-related-img-wrap">
                  <img src={spice.images[0]} alt={spice.name} loading="lazy" />
                </div>
                <span className="kvd-related-category">{spice.category}</span>
                <h4 className="kvd-related-name">{spice.name}</h4>
                <div className="kvd-stars" style={{ justifyContent: "center", marginBottom: 8 }}>
                  {renderStars(spice.rating)}
                </div>
                <div className="kvd-related-price-row">
                  <span className="kvd-related-price">₹{spice.weights[0].price}</span>
                  {spice.weights[0].oldPrice > spice.weights[0].price && (
                    <span className="kvd-related-old">₹{spice.weights[0].oldPrice}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
